import React from 'react';

import introImg from '../../../../assets/images/courses/teamplayer&collaboration/chapter10.1.jpg';
import charIntro from '../../../../assets/images/courses/teamplayer&collaboration/chapter10.2.jpg';
import { course_chat_questions3 } from '../../../../courses/building-teamwork-collaboration/building-teamwork-collaboration-quiz';
import { CourseChapter } from '../../../../courses/courses.types';
import { ActionableBlock } from '../../ActionableBlock';
import { CarouselComponent } from '../../CarouselComponent';
import { InfoBlock } from '../../InfoBlock';
import { MediaBlock } from '../../MediaBlock';

interface TeamPlayersAndCollaborationChapter1 {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersAndCollaborationChapter10: React.FC<
  TeamPlayersAndCollaborationChapter1
> = ({ chapter, onNextChapter, totalPages }) => {
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const slidesData = [
    {
      heading: 'Introduction',
      paragraph: `<p>Here are some key reasons why feedback is important. Click start and the arrows to navigate through the reasons.</p>`,
      list: [],
      image: '',
    },
    {
      heading: 'Promotes Continuous Improvement:',
      paragraph: `<p>Feedback helps team members understand their strengths and areas for improvement. Constructive feedback encourages individuals to develop new skills and refine existing ones, leading to continuous personal and professional growth.</p>`,
      list: [],
      image: '',
    },
    {
      heading: 'Enhances Performance:',
      paragraph: `<p>Regular feedback provides clear guidelines on what is expected and how to meet those expectations. By understanding what they are doing well and what needs improvement, team members can adjust their actions and behaviors to enhance their performance.</p>`,
      list: [],
      image: '',
    },
    {
      heading: 'Builds Trust and Relationships:',
      paragraph: `<p>Open and honest feedback fosters a culture of transparency and trust within the team. When team members feel comfortable giving and receiving feedback, it strengthens relationships and creates a supportive environment where everyone feels valued and understood.</p>`,
      list: [],
      image: '',
    },
    {
      heading: 'Encourages Accountability:',
      paragraph: `<div><p>Feedback holds team members accountable for their actions. By regularly assessing performance and providing feedback, individuals are reminded of their responsibilities and commitments, ensuring that everyone contributes effectively to the team’s goals.</p> </div>`,
      list: [],
      image: '',
    },
    {
      heading: 'Facilitates Better Communication:',
      paragraph: `<p>Effective feedback improves communication within the team. It encourages open dialogue, helps clarify expectations, and addresses any misunderstandings promptly. This leads to more efficient collaboration and problem-solving.</p>`,
      list: [],
      image: '',
    },
    {
      heading: 'Boosts Morale and Motivation:',
      paragraph: `<p>Positive feedback can boost morale by recognizing and appreciating the efforts of team members. When individuals feel acknowledged for their hard work, it increases their motivation and engagement, driving them to continue performing at their best.</p>`,
      list: [],
      image: '',
    },
  ];

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Confidently Navigate Conflict</h2>
        <br />
        <MediaBlock mediaImage={introImg}></MediaBlock>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Dealing with conflicts can be tough, especially when you're working closely with others.
          But learning how to handle these situations can help you build better relationships and
          make teamwork smoother.
        </p>
        <p className="text-lg text-left font-semibold mt-10 mb-10">
          In this section, we'll look at three practical ways to navigate high-friction situations
          and minimize misunderstandings. You'll learn how to communicate assertively, stay curious,
          and offer helpful feedback. These skills will not only help you manage conflicts but also
          improve your overall teamwork and relationships.
        </p>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">
          Practical Ways to Navigate High-friction Situations
        </h3>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Here are three practical ways to navigate high-friction situations to minimize
          misunderstandings and protect the integrity of your relationships.
        </p>
        {/* Horizontal Line   ..................................................................................*/}
        <div className="relative flex items-center justify-center w-full mb-8 mt-8">
          <div className="w-full border-t border-gray-300"></div>
          <div
            className="absolute bg-purple-500 text-white font-bold flex items-center justify-center 
      w-10 h-10 rounded-full text-lg shadow-lg"
          >
            1
          </div>
        </div>
        <br />
        <h2 className="font-semibold text-2xl">Communicating Assertively</h2>
        <br />
        <p className="text-lg text-left font-light mt-10 mb-10">
          How do we speak up and call out someone's behavior in a respectful way? Maintain
          composure. The best way to begin deescalating friction is not to react. Take a moment to
          collect your thoughts.
        </p>
        <ol className="ml-3 list-disc pl-5 mb-14 text-xl font-light">
          <li>
            <p className="ml-3 text-lg font-light flex-1 mt-5 mb-5">Maintain composure</p>
          </li>
          <li>
            <p className="ml-3 text-lg font-light flex-1 mt-5 mb-5">
              Make it about you by using “I” statements
            </p>
          </li>
          <li>
            <p className="ml-3 text-lg font-light flex-1 mt-5 mb-5">
              Meet face-to-face if possible
            </p>
          </li>
          <li>
            <p className="ml-3 text-lg font-light flex-1 mt-5 mb-5">
              Share your interests rather than only your position
            </p>
          </li>
        </ol>
        <p className="text-lg text-left font-light mt-10 mb-10">
          In the moment, if we act on emotion, we might say, "You are frustrating me," or, "You're
          so unreasonable," which is a personal attack. Instead, we recommend using I-statements,
          like, "I'm frustrated because this is important to me."
        </p>
        {/* Horizontal Line   ..................................................................................*/}
        <div className="relative flex items-center justify-center w-full mb-8 mt-8">
          <div className="w-full border-t border-gray-300"></div>
          <div
            className="absolute bg-purple-500 text-white font-bold flex items-center justify-center 
      w-10 h-10 rounded-full text-lg shadow-lg"
          >
            2
          </div>
        </div>
        <br />
        <h2 className="font-semibold text-2xl">Stay Curious</h2>
        <br />
        <p className="text-lg text-left font-light mt-10 mb-10">
          When the stakes are high, it is too easy to misunderstand someone or become emotionally
          triggered by someone's words or behavior. To avoid a defensive reaction, be curious and
          use one of these four phrases. They all start with a neutral first response, for example,
          “That’s interesting”:
        </p>
        <ol className="ml-3 list-disc pl-5 mb-14 text-xl font-light">
          <li>
            <p className="ml-3 text-lg font-light flex-1 mt-5 mb-5">
              “…can you tell me more about that?”
            </p>
          </li>
          <li>
            <p className="ml-3 text-lg font-light flex-1 mt-5 mb-5">“…what makes you say that?”</p>
          </li>
          <li>
            <p className="ml-3 text-lg font-light flex-1 mt-5 mb-5">“…what made you do that?”</p>
          </li>
          <li>
            <p className="ml-3 text-lg font-light flex-1 mt-5 mb-5">“…what made you think that?"</p>
          </li>
        </ol>
        <InfoBlock
          title="Note: "
          text="These prompts are more about the principle than the words themselves. They can help to deescalate high-pressure situations and are a valuable addition to your conflict management toolkit. "
        />
        {/* Horizontal Line   ..................................................................................*/}
        <div className="relative flex items-center justify-center w-full mb-8 mt-8">
          <div className="w-full border-t border-gray-300"></div>
          <div
            className="absolute bg-purple-500 text-white font-bold flex items-center justify-center 
      w-10 h-10 rounded-full text-lg shadow-lg"
          >
            3
          </div>
        </div>
        <br />
        <h2 className="font-semibold text-2xl">Offering Feedback</h2>
        <br />
        <p className="text-lg text-left font-light mt-10 mb-10">
          Feedback is crucial for teams because it fosters growth, improves performance, and
          enhances communication. Here are some key reasons why feedback is important:
        </p>
        <br />
        <div className="border rounded-3xl mr-8">
          <CarouselComponent data={slidesData} isRequired={false} />
        </div>
        <br />
        <h2 className="font-semibold text-2xl">Approaches to Giving Feedback</h2>
        <p className="text-lg text-left font-light mt-10 mb-10">
          Here's a simple five-step brain-friendly approach to giving feedback.
        </p>
        <div className="flex flex-col items-center relative min-h-screen py-10">
          {/* Vertical Line (Stops before the last div) */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gray-200 h-[calc(100%-100px)]"></div>

          {/*drop down Cards */}
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Step 1: Prime with, "Yes."
            </h3>
            <h2 className="font-semibold text-xl">Ask a short question to prime the receiver.</h2>
            <p className="text-xl font-light mt-8 mb-8">
              Ask a short question to prime the receiver. "Do you have five minutes to discuss how
              the last meeting went?" Or, "I have some ideas for how to improve the report. Can I
              share them now?"
            </p>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Step 2: Authentic praise
            </h3>
            <h2 className="font-semibold text-xl">
              Validate something about the person or their approach.
            </h2>
            <p className="text-xl font-light mt-8 mb-8">
              "You've really stepped up over the last two weeks," or, "Thanks for the extra effort
              you've invested lately."
            </p>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Step 3: Provide the data point
            </h3>
            <h2 className="font-semibold text-xl">
              Specifically mention evidence of what happened.
            </h2>
            <p className="text-xl font-light mt-8 mb-8">
              Be specific and factual. "You said you'd provide the report by 11:00 AM and I didn't
              receive it until 3:00 PM."
            </p>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Step 4: Share the impact
            </h3>
            <h2 className="font-semibold text-xl">
              Share how the data point impacted you or the team
            </h2>
            <p className="text-xl font-light mt-8 mb-8">
              Sharing how the data point impacted you or the team provides a sense of meaning and
              reminds them of how their work matters. "Because I didn't receive the report, I
              couldn't submit it on time, which has delayed the project's completion."
            </p>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">Step 5: Check in</h3>
            <h2 className="font-semibold text-xl">
              Check in and end with a collaborative question.
            </h2>
            <p className="text-xl font-light mt-8 mb-8">
              "Were you aware of the impact? What's your take on this?" You might also want to check
              in on the person. "You are usually such an upbeat part of the team. This is
              uncharacteristic of you. Is everything okay?" Or, "How can the team support you?" This
              sparks real talk, a genuine and meaningful conversation.
            </p>
          </div>
        </div>

        <ActionableBlock
          backgroundImage={charIntro}
          courseChatQuestions={course_chat_questions3}
          characterImage={'charIntro'}
          feedbackTitle="Scenario Complete!"
          feedbackText=" "
          buttonName="Continue"
          cssClass="perception-chat"
        />

        <br />
        <p className="text-2xl text-center font-normal mt-5 mb-10">
          Next we will explore two tricky issues to avoid.
        </p>
        <br />
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Avoid Risky Shift & Groupthink
        </button>
      </div>
    </>
  );
};
